import {
	FontIconName,
	MediaPlaceholder,
	MediaPlaceholderSize,
	VideoThumbnail
} from "client/Components";
import {
	getHubImageUrl,
	HubItemMediaKind,
	HubItemMediaPreviewViewModel,
	ImageHubMediaPreviewViewModel,
	VideoHubMediaPreviewViewModel
} from "client/Api/Hub/common";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import styles from "./HubThumbnail.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { never } from "client_legacy/ndaw/errors";
import { getYoutubeVideoThumbnailFromUrl, isYoutubeVideoUrl } from "client/Misc/youTube";
import {
	getVimeoThumbnailUrls,
	isVimeoVideoUrl,
	VimeoThumbnailUrls
} from "client/Misc/vimeo";
import { isNotNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { hubItemKindToHubDataSourceMap } from "../Sidebar/common";
import { ImageWithFocalPoint } from "../FocalPointDialog/ImageWithFocalPoint";

export type HubThumbnailProps = {
	title: string;
	media: HubItemMediaPreviewViewModel;
	hubItemKind: HubItemKind;
	mediaPlaceholderSize?: MediaPlaceholderSize;
};

export const HubThumbnail = (props: HubThumbnailProps): ReactElement | null => {
	const {
		media,
		title,
		hubItemKind,
		mediaPlaceholderSize = "large"
	} = props;

	switch (media.mediaKind) {
		case HubItemMediaKind.Embed:
		case HubItemMediaKind.None:
			return (
				<MediaPlaceholder
					icon={hubItemKindToIcon[hubItemKind]}
					size={mediaPlaceholderSize}
				/>
			);
		case HubItemMediaKind.Image:
			return (
				<HubImage
					media={media}
					title={title}
					hubItemKind={hubItemKind}
					mediaPlaceholderSize={mediaPlaceholderSize}
				/>
			);
		case HubItemMediaKind.Video:
			return (
				<VideoThumbnail
					buttonSize={mediaPlaceholderSize}
				>
					<VideoPoster
						media={media}
						size={mediaPlaceholderSize}
					/>
				</VideoThumbnail>
			);
		default:
			return never(media);
	}
};

HubThumbnail.displayName = "HubThumbnail";

export const hubItemKindToIcon: {[key in HubItemKind]: FontIconName} = {
	[HubItemKind.Announcement]: "bullhorn",
	[HubItemKind.KnowledgeItem]: "book",
	[HubItemKind.Event]: "calendar-star"
};

type HubImageProps = {
	media: ImageHubMediaPreviewViewModel;
	title: string;
	hubItemKind: HubItemKind;
	mediaPlaceholderSize: MediaPlaceholderSize;
};

const HubImage = (props: HubImageProps): ReactElement => {
	const {
		media,
		title,
		hubItemKind,
		mediaPlaceholderSize
	} = props;

	const [hasError, setHasError] = useState(false);

	if (hasError) {
		return (
			<MediaPlaceholder
				icon={hubItemKindToIcon[hubItemKind]}
				size={mediaPlaceholderSize}
			/>
		);
	}

	return (
		<ImageWithFocalPoint
			src={getHubImageUrl(hubItemKindToHubDataSourceMap[hubItemKind], media.imageId)}
			alt={title}
			height="fill"
			focalPoint={media.focalPoint}
			onError={() => setHasError(true)}
		/>
	);
};

HubImage.displayName = "HubImage";

type VideoPosterProps = {
	media: VideoHubMediaPreviewViewModel;
	size: MediaPlaceholderSize;
};

const VideoPoster = (props: VideoPosterProps): ReactElement => {
	const {
		media,
		size
	} = props;

	const [posterUrl, setPosterUrl] = useState<string | null>(null);

	useEffect(
		(): void | (() => void)=> {
			const { videoUrl } = media;

			async function getVideoUrl(signal: AbortSignal): Promise<void> {
				try {
					if (isYoutubeVideoUrl(videoUrl)) {
						setPosterUrl(
							getYoutubeVideoThumbnailFromUrl(videoUrl)
						);
					} else if (isVimeoVideoUrl(videoUrl)) {
						const urls: VimeoThumbnailUrls = await getVimeoThumbnailUrls(videoUrl, signal);

						setPosterUrl(
							urls[mediaPlaceholderSizeToVimeoThumbnailSize[size]]
						);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			};

			const cancellation = new AbortController();
			getVideoUrl(cancellation.signal);

			return () => cancellation.abort();
		},
		[media, size]
	);

	return (
		<div
			className={styles.videoThumbnail}
			style={{
				backgroundImage: isNotNil(posterUrl) ? `url(${posterUrl})`: undefined
			}}
		/>
	);
};

VideoPoster.displayName = "VideoPoster";

const mediaPlaceholderSizeToVimeoThumbnailSize: { [key in MediaPlaceholderSize]: keyof VimeoThumbnailUrls; } = {
	["extra-small"]: "small",
	["small"]: "small",
	["medium"]: "medium",
	["large"]: "large",
	["extra-large"]: "large",
};